import { useState, useEffect, lazy, Suspense } from "react";
import "@/App.css";

// Suppress ResizeObserver loop error (harmless, caused by third-party widgets like Elfsight)
const originalError = window.onerror;
window.onerror = (msg, ...args) => {
  if (msg && typeof msg === 'string' && msg.includes('ResizeObserver loop')) return true;
  return originalError ? originalError(msg, ...args) : false;
};
window.addEventListener('error', (e) => {
  if (e.message?.includes('ResizeObserver loop')) { e.stopPropagation(); e.preventDefault(); }
});

import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { HelmetProvider } from "react-helmet-async";

// Analytics tracking
import { initAllTracking, trackPageView, trackVisitorBehavior } from "@/utils/analytics";
// hasConsented stays as a sync import (used by initAllTracking synchronously).
// The visual banner component itself is deferred behind an idle-mount gate
// so it never contends with the hero for main-thread time during LCP.
import { hasConsented } from "@/components/CookieConsentBanner";
const CookieConsentBanner = lazy(() => import("@/components/CookieConsentBanner"));
const QuizStickyPill = lazy(() => import("@/components/QuizStickyPill"));
import SeoRedirects, { mapLegacyPath } from "@/components/SeoRedirects";

// Aug 2026: canonical service URL migrated from /handyman-services/* to
// /services/*. This tiny component client-side 301-equivalents any legacy
// hit so external inbound links (old Google index, old email/social
// posts, printed marketing collateral) still land on the right page.
// Preserves query string (utm, gclid, opref) so paid-traffic attribution
// isn't lost during the crawl transition window. First honours the
// EXACT_REDIRECTS table in SeoRedirects so legacy slugs that hop to a
// different destination (e.g. /handyman-services/home-maintenance-plans
// → /maintenance-membership) still land correctly.
function HandymanServicesRedirect() {
  const { serviceSlug } = useParams();
  const location = useLocation();
  const legacyPath = `/handyman-services/${serviceSlug}`;
  const exactMapped = mapLegacyPath(legacyPath);
  const target = exactMapped && exactMapped !== legacyPath
    ? exactMapped
    : `/services/${serviceSlug}`;
  return <Navigate to={target + (location.search || "")} replace />;
}
const CustomerReviewsPage = lazy(() => import("@/pages/CustomerReviewsPage"));
const ScorecardQuiz = lazy(() => import("@/pages/ScorecardQuiz"));
// SEO
import { LocalBusinessSchema } from "@/components/SEO";

import Header from "@/components/Header";
const HomePage = lazy(() => import("@/pages/HomePage"));
const IntakeForm = lazy(() => import("@/pages/IntakeForm"));
const CustomerPortal = lazy(() => import("@/pages/CustomerPortal"));
const ServicesPage = lazy(() => import("@/pages/ServicesPage"));
const BookingsPage = lazy(() => import("@/pages/BookingsPage"));
const MessagesPage = lazy(() => import("@/pages/MessagesPage"));
const LoyaltyPage = lazy(() => import("@/pages/LoyaltyPage"));
const RewardsProgramPage = lazy(() => import("@/pages/RewardsProgramPage"));
const WinBackOffer = lazy(() => import("@/pages/WinBackOffer"));
const SmartEstimatorV2 = lazy(() => import("@/pages/SmartEstimatorV2"));
const BonusCalcV2 = lazy(() => import("@/pages/BonusCalcV2"));
const V2StatementMagicLink = lazy(() => import("@/pages/V2StatementMagicLink"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const AdminCommunications = lazy(() => import("@/pages/AdminCommunications"));
const AdminLeads = lazy(() => import("@/pages/AdminLeads"));
const AdminChecklistLeads = lazy(() => import("@/pages/AdminChecklistLeads"));
const AdminSettings = lazy(() => import("@/pages/AdminSettings"));
const AdminEmailActivity = lazy(() => import("@/pages/AdminEmailActivity"));
const AdminSlackChannels = lazy(() => import("@/pages/AdminSlackChannels"));
const AdminHomeDepotPurchases = lazy(() => import("@/pages/AdminHomeDepotPurchases"));
const AdminMaintenanceProposalsList = lazy(() => import("@/pages/AdminMaintenanceProposalsList"));
const AdminCustomerHistory = lazy(() => import("@/pages/AdminCustomerHistory"));
const AdminLSALeads = lazy(() => import("@/pages/AdminLSALeads"));
const AdminServiceArea = lazy(() => import("@/pages/AdminServiceArea"));
const AdminPipelineGuide = lazy(() => import("@/pages/AdminPipelineGuide"));
const LandingHandyman = lazy(() => import("@/pages/LandingHandyman"));
const AdminLandingAnalytics = lazy(() => import("@/pages/AdminLandingAnalytics"));
const AdminLocalSeoReport = lazy(() => import("@/pages/AdminLocalSeoReport"));
// Aug 2026 — GEO Sprint 3 dashboard (Phase 10 + 11).
const AdminAIVisibility = lazy(() => import("@/pages/AdminAIVisibility"));
const AdminGbp = lazy(() => import("@/pages/AdminGbp"));
const AdminLocalSeoProjects = lazy(() => import("@/pages/AdminLocalSeoProjects"));
const AdminLocalAuthority = lazy(() => import("@/pages/AdminLocalAuthority"));
const ProjectCaseStudyPage = lazy(() => import("@/pages/ProjectCaseStudyPage"));
const AdminLocalSeoPhotos = lazy(() => import("@/pages/AdminLocalSeoPhotos"));
const AdminSops = lazy(() => import("@/pages/AdminSops"));
const AdminSopCompliance = lazy(() => import("@/pages/AdminSopCompliance"));
const TechnicianSops = lazy(() => import("@/pages/TechnicianSops"));
const TermsOfService = lazy(() => import("@/pages/TermsOfService"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const GiftCardsPage = lazy(() => import("@/pages/GiftCardsPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const AdminReviews = lazy(() => import("@/pages/AdminReviews"));
const MaintenanceMembershipPage = lazy(() => import("@/pages/MaintenanceMembershipPage"));
const MaintenanceProposalView = lazy(() => import("@/pages/MaintenanceProposalView"));
const FortySevenPointChecklistPage = lazy(() => import("@/pages/FortySevenPointChecklistPage"));
const QuoContactApprovalPage = lazy(() => import("@/pages/QuoContactApprovalPage"));
const AdminQuoContactApprovalsPage = lazy(() => import("@/pages/AdminQuoContactApprovalsPage"));
const AdminPartnerInvitations = lazy(() => import("@/pages/AdminPartnerInvitations"));
const AdminMaintenanceProposalBuilder = lazy(() => import("@/pages/AdminMaintenanceProposalBuilder"));
const PropertyMaintenanceTermsPage = lazy(() => import("@/pages/PropertyMaintenanceTermsPage"));
const PropertyHealthCheckPage = lazy(() => import("@/pages/PropertyHealthCheckPage"));
const HowCreditsWorkPage = lazy(() => import("@/pages/HowCreditsWorkPage"));
const TVMountingCalculator = lazy(() => import("@/pages/TVMountingCalculator"));
const PressureWashingCalculator = lazy(() => import("@/pages/PressureWashingCalculator"));
const CartPage = lazy(() => import("@/pages/CartPage"));
const MyEstimatesPage = lazy(() => import("@/pages/MyEstimatesPage"));
const GuestEstimateReviewPage = lazy(() => import("@/pages/GuestEstimateReviewPage"));
const ZellePaymentPage = lazy(() => import("@/pages/ZellePaymentPage"));
const AdminZelleSettings = lazy(() => import("@/pages/AdminZelleSettings"));
const AdminSeasonalBanner = lazy(() => import("@/pages/AdminSeasonalBanner"));
const StormReadyPage = lazy(() => import("@/pages/StormReadyPage"));
const StormReadyHarborPage = lazy(() => import("@/pages/StormReadyHarborPage"));
const StormReadyReleasePage = lazy(() => import("@/pages/StormReadyReleasePage"));
const StormReadyApprovePage = lazy(() => import("@/pages/StormReadyApprovePage"));
const AdminStormReadyLeads = lazy(() => import("@/pages/AdminStormReadyLeads"));
const AdminStormReadyV1 = lazy(() => import("@/pages/AdminStormReadyV1"));
const AdminStormReadyGuide = lazy(() => import("@/pages/AdminStormReadyGuide"));
const AdminLocalSeo = lazy(() => import("@/pages/AdminLocalSeo"));
const AdminStormReadySettings = lazy(() => import("@/pages/AdminStormReadySettings"));
const AdminStormReadyBuilder = lazy(() => import("@/pages/AdminStormReadyBuilder"));
const AdminHelpBotTraining = lazy(() => import("@/pages/AdminHelpBotTraining"));
const AdminSonaKnowledge = lazy(() => import("@/pages/AdminSonaKnowledge"));
const AdminSonaCalls = lazy(() => import("@/pages/AdminSonaCalls"));
const AdminNoAI = lazy(() => import("@/pages/AdminNoAI"));
const BecomePartnerPage = lazy(() => import("@/pages/BecomePartnerPage"));
const PartnerPortalPage = lazy(() => import("@/pages/PartnerPortalPage"));
const ReferFriendPage = lazy(() => import("@/pages/ReferFriendPage"));
const PayoutDashboard = lazy(() => import("@/pages/PayoutDashboard"));
const AdminCouponsPage = lazy(() => import("@/pages/AdminCouponsPage"));
const AdminBonusCalc = lazy(() => import("@/pages/AdminBonusCalc"));
const SmartEstimatePage = lazy(() => import("@/pages/SmartEstimatePage"));
const EmployeeBonusPortal = lazy(() => import("@/pages/EmployeeBonusPortal"));
const TechField = lazy(() => import("@/pages/TechField"));
const FindAProPage = lazy(() => import("@/pages/FindAProPage"));
const ServiceAreasPage = lazy(() => import("@/pages/ServiceAreasPage"));
const CityServicePage = lazy(() => import("@/pages/CityServicePage"));
const ServiceDetailPage = lazy(() => import("@/pages/ServiceDetailPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const BlogPage = lazy(() => import("@/pages/BlogPage"));
const BlogPostPage = lazy(() => import("@/pages/BlogPostPage"));
const FAQPage = lazy(() => import("@/pages/FAQPage"));
const AdminBlogDrafts = lazy(() => import("@/pages/AdminBlogDrafts"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const AdminGuideHub = lazy(() => import("@/pages/AdminGuideHub"));
const AdminCallbackManagement = lazy(() => import("@/pages/AdminCallbackManagement"));
const AdminRevenueForensics = lazy(() => import("@/pages/AdminRevenueForensics"));
const AdminSMSSettings = lazy(() => import("@/pages/AdminSMSSettings"));
const AdminCallAnalytics = lazy(() => import("@/pages/AdminCallAnalytics"));
const AdminAIAgent = lazy(() => import("@/pages/AdminAIAgent"));
const AdminTurnoInspections = lazy(() => import("@/pages/AdminTurnoInspections"));
const EmployeeWeekendAvailability = lazy(() => import("@/pages/EmployeeWeekendAvailability"));
const AdminWeekendRewards = lazy(() => import("@/pages/AdminWeekendRewards"));
const AdminDailyKPIs = lazy(() => import("@/pages/AdminDailyKPIs"));
const BookingFunnelPage = lazy(() => import("@/pages/BookingFunnelPage"));
const AdminArAging = lazy(() => import("@/pages/AdminArAging"));
const AdminChristinaReportCard = lazy(() => import("@/pages/AdminChristinaReportCard"));
const AdminEquipmentRegistry = lazy(() => import("@/pages/AdminEquipmentRegistry"));
const InventoryPage = lazy(() => import("@/pages/Inventory"));
const InventoryLabel = lazy(() => import("@/pages/Inventory/Label"));
const InventoryMobile = lazy(() => import("@/pages/Inventory/Mobile"));
const AdminFleetTracker = lazy(() => import("@/pages/AdminFleetTracker"));
const AdminCallbackAnalytics = lazy(() => import("@/pages/AdminCallbackAnalytics"));
import ErrorBoundary from "@/components/ErrorBoundary";
const AdminTurnoBilling = lazy(() => import("@/pages/AdminTurnoBilling"));
const TurnoNicknameFixer = lazy(() => import("@/pages/TurnoNicknameFixer"));
const AdminBonusCalcV11 = lazy(() => import("@/pages/AdminBonusCalcV1_1"));
const AdminTechTimeReport = lazy(() => import("@/pages/AdminTechTimeReport"));
const AdminSignals = lazy(() => import("@/pages/AdminSignals"));
const AdminPropertyNicknames = lazy(() => import("@/pages/AdminPropertyNicknames"));
const AdminOpsCutover = lazy(() => import("@/pages/AdminOpsCutover"));
const AdminGoLive = lazy(() => import("@/pages/AdminGoLive"));
const AdminSecurity = lazy(() => import("@/pages/AdminSecurity"));
import AskOCTaskerWidget from "@/components/signals/AskOCTaskerWidget";
const AdminPipeline = lazy(() => import("@/pages/AdminPipeline"));
const AdminUserManagement = lazy(() => import("@/pages/AdminUserManagement"));
const AdminRewardsSignups = lazy(() => import("@/pages/AdminRewardsSignups"));
const AdminIntegrationSecrets = lazy(() => import("@/pages/AdminIntegrationSecrets"));
const AdminCutoverGuide = lazy(() => import("@/pages/AdminCutoverGuide"));
const AdminOperationsGuide = lazy(() => import("@/pages/AdminOperationsGuide"));
const TechnicianGuide = lazy(() => import("@/pages/TechnicianGuide"));
const CompanyDash = lazy(() => import("@/pages/CompanyDash"));
const CompanyDashTv = lazy(() => import("@/pages/CompanyDashTv"));
const RouteOSLogin = lazy(() =>
  import("@/pages/routeos/RouteOSApp").then((m) => ({ default: m.RouteOSLoginPage })),
);
const RouteOSTechnicians = lazy(() =>
  import("@/pages/routeos/RouteOSApp").then((m) => ({ default: m.RouteOSTechniciansPage })),
);
const RouteOSPlatform = lazy(() =>
  import("@/pages/routeos/RouteOSApp").then((m) => ({ default: m.RouteOSPlatformAdminPage })),
);
const RouteOSSkills = lazy(() => import("@/pages/routeos/SkillsPage"));
const RouteOSVehicles = lazy(() => import("@/pages/routeos/VehiclesPage"));
const RouteOSCustomers = lazy(() => import("@/pages/routeos/CustomersPage"));
const RouteOSProperties = lazy(() => import("@/pages/routeos/PropertiesPage"));
const RouteOSJobs = lazy(() => import("@/pages/routeos/JobsPage"));
const RouteOSRules = lazy(() => import("@/pages/routeos/RulesPage"));
const RouteOSSmartFit = lazy(() => import("@/pages/routeos/SmartFitPage"));
const RouteOSBookingAdmin = lazy(() => import("@/pages/routeos/BookingAdminPage"));
const RouteOSSettings = lazy(() => import("@/pages/routeos/TenantAdminPage"));
const RouteOSPublicBooking = lazy(() => import("@/pages/routeos/PublicBookingPage"));
const RouteOSPublicBookingV2 = lazy(() => import("@/pages/routeos/PublicBookingV2Page"));
const FreeEstimateLanding = lazy(() => import("@/pages/FreeEstimateLandingPage"));
const AdminWebsiteAnalytics = lazy(() => import("@/pages/AdminWebsiteAnalytics"));
const AdminTeamStructure = lazy(() => import("@/pages/AdminTeamStructure"));
const AdminHcpEmployeeMapping = lazy(() => import("@/pages/AdminHcpEmployeeMapping"));
const AdminCampaigns = lazy(() => import("@/pages/AdminCampaigns"));
const AdminGoogleReviews = lazy(() => import("@/pages/AdminGoogleReviews"));
const AdminScorecard = lazy(() => import("@/pages/AdminScorecard"));
const AdminSocialPublish = lazy(() => import("@/pages/AdminSocialPublish"));
const AdminBonusCalcV11Shadow = lazy(() => import("@/pages/AdminBonusCalcV11"));
const AdminBonusCalcV11Live = lazy(() => import("@/pages/AdminBonusCalcV11Live"));
const AdminBonusCalcLive = lazy(() => import("@/pages/AdminBonusCalcLive"));
const AdminMarketplace = lazy(() => import("@/pages/AdminMarketplace"));
const AdminBilling = lazy(() => import("@/pages/AdminBilling"));
const AdminRevenueIntelligence = lazy(() => import("@/pages/AdminRevenueIntelligence"));
const AdminTodo = lazy(() => import("@/pages/AdminTodo"));
const AdminIntegrationsHealth = lazy(() => import("@/pages/AdminIntegrationsHealth"));
const AdminAISafety = lazy(() => import("@/pages/AdminAISafety"));
const AdminIntentAnalytics = lazy(() => import("@/pages/AdminIntentAnalytics"));
const AdminTimekeeping = lazy(() => import("@/pages/AdminTimekeeping"));
const AdminTimeOff = lazy(() => import("@/pages/AdminTimeOff"));
const AdminJobHoursAudit = lazy(() => import("@/pages/AdminJobHoursAudit"));
const AdminTurnoQuickMap = lazy(() => import("@/pages/AdminTurnoQuickMap"));
const AdminQuickMapQueue = lazy(() => import("@/pages/AdminQuickMapQueue"));
const AdminPlanTomorrow = lazy(() => import("@/pages/AdminPlanTomorrow"));
const AdminHcpMapping = lazy(() => import("@/pages/AdminHcpMapping"));
const AdminIntelligence = lazy(() => import("@/pages/AdminIntelligence"));
const AdminWhiteLabel = lazy(() => import("@/pages/AdminWhiteLabel"));
const AdminTechAssistant = lazy(() => import("@/pages/AdminTechAssistant"));
const AdminAIAuditLog = lazy(() => import("@/pages/AdminAIAuditLog"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
const GettingStartedPage = lazy(() => import("@/pages/GettingStartedPage"));
const WindowCleaningPage = lazy(() => import("@/pages/WindowCleaningPage"));
const CheckoutSuccessPage = lazy(() => import("@/pages/CheckoutSuccessPage"));
const CheckoutCancelledPage = lazy(() => import("@/pages/CheckoutCancelledPage"));
const EstimateDetailPage = lazy(() => import("@/pages/EstimateDetailPage"));
const BonusGuide = lazy(() => import("@/pages/BonusGuide"));
import AuthModal from "@/components/AuthModal";
// Modals below are deferred — they render only when their `open` flag is
// true (post-interaction). Lazy-loading keeps them out of the initial
// bundle so mobile LCP doesn't pay for chunks the user may never see.
const FirstLoginModal = lazy(() => import("@/components/FirstLoginModal"));
const WelcomeWhiteLabelModal = lazy(() => import("@/components/WelcomeWhiteLabelModal"));
const HomeProfileModal = lazy(() => import("@/components/HomeProfileModal"));
// Chatbot deferred — customer-facing chat widget is heavy (AI backend
// wiring, streaming reply state, avatar images) and lives fixed bottom-
// right, meaning it has zero LCP contribution. Load it only after the
// hero has painted (idle-mount gate below).
const Chatbot = lazy(() => import("@/components/Chatbot"));
import Footer from "@/components/Footer";
// EmailCapturePopup + PWA install prompts all render behind timers/cooldowns
// well after LCP — no reason to ship them in the initial homepage chunk.
const EmailCapturePopup = lazy(() => import("@/components/EmailCapturePopup"));
const InstallPrompt = lazy(() => import("@/components/InstallPrompt"));
const CustomerInstallPrompt = lazy(() => import("@/components/CustomerInstallPrompt"));
import { CartProvider } from "@/context/CartContext";
import { ThemeProvider } from "@/components/ThemeProvider";
import ServiceWorkerBootstrap from "@/components/ServiceWorkerBootstrap";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const axiosInstance = axios.create({
  baseURL: API,
  withCredentials: true, // send httpOnly auth cookie on every request
});

// Kept for transition: if a legacy caller stashed a token in localStorage
// (e.g. before the Apr 2026 cookie migration), attach it as a Bearer header.
// New code doesn't setItem "token" anymore — the cookie is the source of
// truth — so this interceptor is a no-op for fresh sessions.
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Attach session id so backend can attribute conversions to landing pages.
  // Same UUID lives in sessionStorage and is set by analytics.js::trackPageView.
  try {
    let sid = sessionStorage.getItem("oct_sid");
    if (!sid) {
      sid = (window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `s-${Date.now()}-${Math.random().toString(36).slice(2)}`);
      sessionStorage.setItem("oct_sid", sid);
    }
    config.headers["X-Session-Id"] = sid;
  } catch { /* sessionStorage may be unavailable in private mode */ }
  // Impersonation: if the user picked "view as another tech" the target
  // user_id lives in sessionStorage. Backend `get_current_user` swaps the
  // authenticated user to that target if the caller is authorized
  // (admin or in-downline manager). See /app/backend/dependencies.py.
  // Individual callers can opt-out by explicitly setting the header on
  // their request config (even to empty string) — e.g. the ViewingAsPicker
  // fetching /users/viewable always wants the caller's own downline.
  try {
    const explicit = config.headers?.["X-Impersonate-User-Id"];
    if (explicit === undefined) {
      const impId = sessionStorage.getItem("oct_impersonate_uid");
      if (impId) config.headers["X-Impersonate-User-Id"] = impId;
    } else if (explicit === "") {
      // Explicit empty string → strip the header so the request runs
      // as the real authenticated caller, not the impersonated target.
      delete config.headers["X-Impersonate-User-Id"];
    }
  } catch { /* no-op */ }
  return config;
});

// Same default for plain axios.* calls, so callers that forget to use
// axiosInstance still send credentials.
axios.defaults.withCredentials = true;

// Scroll to top on route change + fire first-party page view
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    // Fire on the next tick so document.title has been updated by Helmet
    setTimeout(() => trackPageView(document.title), 50);
  }, [pathname]);
  return null;
}

// Footer wrapper - hides on admin, portal, and SMS pages
function FooterWrapper({ user, onShowRegister }) {
  const location = useLocation();
  const hideOnPaths = ["/admin", "/portal", "/employee", "/login", "/landing/", "/company-dash/tv", "/stormready/release/", "/stormready/approve/"];
  const isSMS = location.search.includes('source=sms');
  const isHidden = isSMS || hideOnPaths.some(p => location.pathname.startsWith(p));
  if (isHidden) return null;
  return <Footer user={user} onShowRegister={onShowRegister} />;
}

// Header wrapper — hides on focused-conversion landing pages so paid traffic
// has no escape routes (best-practice for Google Ads landing pages).
// Also hides on the TV/wall-display mode of the Company Dash so the display
// is truly fullscreen with no nav chrome.
function HeaderWrapper(props) {
  const location = useLocation();
  const hidePathPrefixes = ["/landing/", "/company-dash/tv", "/stormready/release/", "/stormready/approve/"];
  if (hidePathPrefixes.some((p) => location.pathname.startsWith(p))) return null;
  return <Header {...props} />;
}

// Chatbot wrapper - hides on SMS estimate pages, for admins, and for technicians
// (field staff don't need a customer-facing chatbot interrupting their work)
function ChatbotWrapper({ user }) {
  const location = useLocation();
  const isSMS = location.search.includes('source=sms');
  if (isSMS) return null;
  // Hide for any authenticated staff — admins or technicians.
  if (user?.is_admin) return null;
  if (user?.is_technician) return null;
  // Also hide on staff-only pages even for non-staff (defense in depth).
  const staffPaths = ["/admin", "/employee-portal", "/field", "/company-dash", "/stormready/release/", "/stormready/approve/"];
  if (staffPaths.some(p => location.pathname.startsWith(p))) return null;
  return <Chatbot user={user} />;
}

// Simple component to prompt login for Employee Portal
function EmployeePortalLogin({ onShowAuth }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
        <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Employee Portal</h1>
        <p className="text-gray-600 mb-6">
          Sign in with your OC Tasker employee account to access your bonus dashboard, earnings history, and more.
        </p>
        <button
          onClick={onShowAuth}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
        >
          Sign In to Continue
        </button>
        <p className="text-sm text-gray-500 mt-4">
          Need help? Contact your administrator.
        </p>
      </div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login"); // "login" or "register"
  const [showFirstLoginModal, setShowFirstLoginModal] = useState(false);
  const [showCutoverWelcome, setShowCutoverWelcome] = useState(false);
  const [showOpsWelcome, setShowOpsWelcome] = useState(false);
  const [showTechWelcome, setShowTechWelcome] = useState(false);
  const [showHomeProfileModal, setShowHomeProfileModal] = useState(false);
  const [activeHome, setActiveHome] = useState(null); // Track active home for calculators
  const [userPerms, setUserPerms] = useState(null); // { role: "master", permissions: {...} }

  // Idle-mount gate for LCP-heavy but non-critical widgets (Chatbot,
  // CookieConsentBanner). They render only after the browser has finished
  // painting the hero — either 800ms post-load, or upon first user
  // interaction (scroll/click/key/touch), whichever comes first. This
  // removes ~120-180ms of TBT from the mobile Lighthouse trace without
  // making the widgets feel late (both appear within one scroll tick).
  const [deferredMountsReady, setDeferredMountsReady] = useState(false);
  useEffect(() => {
    if (deferredMountsReady) return;
    let cancelled = false;
    const trigger = () => {
      if (cancelled) return;
      cancelled = true;
      setDeferredMountsReady(true);
    };
    // Timer branch: 800ms after `load` (or now if load already fired)
    const startTimer = () => setTimeout(trigger, 800);
    if (document.readyState === "complete") startTimer();
    else window.addEventListener("load", startTimer, { once: true });
    // Interaction branch: mount immediately on any user gesture
    const evts = ["scroll", "pointerdown", "keydown", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, trigger, { once: true, passive: true, capture: true }));
    return () => {
      cancelled = true;
      window.removeEventListener("load", startTimer);
      evts.forEach((e) => window.removeEventListener(e, trigger, { capture: true }));
    };
  }, [deferredMountsReady]);

  // Token used by legacy admin pages that take a `token` prop and pass it as
  // a Bearer header in raw fetch/axios calls. Sourced from localStorage where
  // login.handleLogin stashes it as a Cloudflare-cookie-strip fallback. Read
  // here once per render so all admin routes share the same value.
  const authToken = typeof window !== "undefined" ? (localStorage.getItem("token") || "") : "";

  const openAuthModal = (mode = "login") => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };
  
  // Handle home change from header
  const handleHomeChange = (homeId, homeData) => {
    setActiveHome(homeData);
  };

  useEffect(() => {
    // Initialize analytics only if user already consented (returning visitor)
    if (hasConsented()) {
      initAllTracking();
    }
    
    // Always try /auth/me — the httpOnly auth cookie will authenticate us
    // on page refresh without us needing to read any browser-accessible token.
    // (Pre-cookie-migration sessions fall back to a Bearer header via the
    //  axios interceptor, so older localStorage tokens still work until they
    //  expire and the user logs back in.)
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axiosInstance.get("/auth/me");
      // Also fetch role flags so is_technician survives page reloads
      let roles = {};
      try {
        const r = await axiosInstance.get("/auth/me/roles");
        roles = r.data || {};
      } catch { /* ignore */ }
      setUser({ ...response.data, is_technician: !!roles.is_technician });
      // Fetch permissions if admin
      if (response.data?.is_admin) {
        try {
          const permRes = await axiosInstance.get("/user-management/my-permissions");
          setUserPerms(permRes.data);
        } catch (e) { /* non-admin or endpoint not available */ }
      }
    } catch (error) {
      // 401 on /auth/me just means "not logged in" — normal for public visitors.
      // Clear any stale legacy token so subsequent requests don't send a bad
      // Authorization header alongside the (now-missing) cookie.
      localStorage.removeItem("token");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, _token, loginResponse = {}) => {
    // Auth tokens are delivered both as an httpOnly cookie (preferred, XSS-safe)
    // AND in the JSON response body (`token`). On production behind Cloudflare,
    // the cookie occasionally fails to round-trip cleanly; localStorage acts as
    // a belt-and-suspenders fallback so /auth/me still authenticates via the
    // Bearer-header interceptor in axiosInstance.
    const tokenFromResponse = loginResponse?.token || _token;
    if (tokenFromResponse) {
      try { localStorage.setItem("token", tokenFromResponse); } catch { /* private mode */ }
    }
    // Merge is_technician flag so downstream (chatbot suppression, field routing) can read it.
    const enrichedUser = { ...userData, is_technician: !!loginResponse?.is_technician };
    setUser(enrichedUser);
    setShowAuthModal(false);
    
    // Fetch permissions for admins
    if (userData?.is_admin) {
      axiosInstance.get("/user-management/my-permissions")
        .then(res => setUserPerms(res.data))
        .catch(() => {});
    }
    
    // Check if first login setup is required (imported users)
    if (loginResponse.requires_first_login) {
      setShowFirstLoginModal(true);
      toast.info("Please complete your account setup");
    } else if (loginResponse.is_new_registration && !userData.home_profile_completed) {
      // Show home profile modal for new registrations
      setTimeout(() => {
        setShowHomeProfileModal(true);
      }, 1500); // Delay to let any animation complete
      toast.success("Welcome to OC Tasker!");
    } else {
      toast.success("Welcome back!");
      // Show cutover guide welcome to admins who haven't seen it yet.
      // Skips technicians, customers, and admins who already dismissed it.
      const seenCutover = loginResponse?.onboarding_seen?.cutover_guide;
      const seenOps     = loginResponse?.onboarding_seen?.ops_guide;
      const seenTech    = loginResponse?.onboarding_seen?.tech_guide;
      if (userData?.is_admin && !seenCutover) {
        setTimeout(() => setShowCutoverWelcome(true), 800);
      } else if (userData?.is_admin && !seenOps) {
        // Admin already finished cutover — offer the Operations playbook next
        setTimeout(() => setShowOpsWelcome(true), 800);
      } else if (loginResponse?.is_technician && !seenTech) {
        // Tech first-login → show the field guide tour
        setTimeout(() => setShowTechWelcome(true), 800);
      }
      // Smart post-login routing based on role
      // Admins → Admin Dashboard · Technicians → Field Dashboard (time clock + jobs only, safe around customers)
      const path = window.location.pathname;
      // Note: /employee-portal is intentionally NOT in this list for technicians —
      // they should be routed to /field (their real dashboard), not the Bonus Portal.
      const alreadyOnAppPage = path.startsWith("/admin") || path.startsWith("/portal") || path.startsWith("/field");
      if (!alreadyOnAppPage) {
        if (userData?.is_admin) {
          window.location.href = "/admin";
        } else if (loginResponse?.is_technician) {
          window.location.href = "/field";
        }
      }
    }
  };
  
  const handleFirstLoginComplete = async () => {
    setShowFirstLoginModal(false);
    // Refresh user data
    await fetchCurrentUser();
    toast.success("Account setup complete! Enjoy your $25 bonus!");
    // Also show home profile modal after first login complete
    setTimeout(() => {
      setShowHomeProfileModal(true);
    }, 1000);
  };
  
  const handleHomeProfileComplete = async () => {
    setShowHomeProfileModal(false);
    // Refresh user data to get updated profile
    await fetchCurrentUser();
  };
  
  const handleHomeProfileSkip = () => {
    setShowHomeProfileModal(false);
  };

  const handleLogout = async () => {
    // Tell the backend to clear the httpOnly cookie — this is the *only* way
    // to remove it (JS can't touch httpOnly cookies).
    try {
      await axiosInstance.post("/auth/logout");
    } catch { /* already signed out / network hiccup — proceed anyway */ }
    // Clear any legacy localStorage token left over from the pre-cookie era.
    localStorage.removeItem("token");
    setUser(null);
    setUserPerms(null);
    toast.success("Logged out successfully");
  };

  // Permission check helper — Master Admin always passes
  const hasPerm = (moduleKey) => {
    if (!user?.is_admin) return false;
    if (!userPerms) return user?.is_admin; // Fallback while loading
    if (userPerms.role === "master") return true;
    return !!userPerms.permissions?.[moduleKey];
  };

  // Listen for custom auth events (from IntakeForm sign-in prompts)
  useEffect(() => {
    const handleOpenAuth = (e) => {
      openAuthModal(e.detail?.mode || "login");
    };
    window.addEventListener('openAuth', handleOpenAuth);
    return () => window.removeEventListener('openAuth', handleOpenAuth);
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <HelmetProvider>
    <ThemeProvider>
    <div className="App">
      <LocalBusinessSchema />
      <CartProvider>
        <BrowserRouter>
          <ScrollToTop />
          <HeaderWrapper
            user={user} 
            onLogout={handleLogout} 
            onShowAuth={() => openAuthModal("login")}
            onShowRegister={() => openAuthModal("register")}
            onHomeChange={handleHomeChange}
          />
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-white"><div className="text-orange-500 animate-pulse text-sm font-medium">Loading…</div></div>}>
          <Routes>
            <Route path="/" element={
              // Technicians always land on Field Dashboard — that's their workspace.
              // Admins keep access to the customer-facing `/` for QA/preview.
              user?.is_technician ? <Navigate to="/field" replace /> :
              user ? <CustomerPortal user={user} onRefresh={fetchCurrentUser} /> :
              <HomePage user={user} onShowAuth={() => openAuthModal("login")} onShowRegister={() => openAuthModal("register")} />
            } />
            <Route path="/get-estimate" element={<IntakeForm user={user} />} />
            {/* /contact used to render a lightweight contact form that only
                emailed Info@octasker.com — it bypassed the intake pipeline
                (no HCP contact, no lead row, no photos). Feb 2026: consolidated
                onto the Free Estimate wizard so every prospective customer
                lands in the same pipeline. 301 preserves SEO for the old URL. */}
            <Route path="/contact" element={<Navigate to="/get-estimate" replace />} />
            <Route path="/customer-reviews" element={<CustomerReviewsPage />} />
            <Route path="/quiz" element={<ScorecardQuiz />} />
            <Route path="/scorecard" element={<Navigate to="/quiz" replace />} />
            <Route path="/go" element={<Navigate to="/get-estimate?source=sms" replace />} />
            {/* Google Ads landing pages — same URL as paid traffic targets,
                with optional /:variant for A/B angle. NO top nav, single CTA. */}
            <Route path="/landing/handyman" element={<LandingHandyman />} />
            <Route path="/landing/handyman/" element={<LandingHandyman />} />
            <Route path="/landing/handyman/:variant" element={<LandingHandyman />} />
            <Route path="/services" element={<ServicesPage user={user} onShowAuth={() => openAuthModal("login")} onShowRegister={() => openAuthModal("register")} onUserUpdate={setUser} />} />
          <Route 
            path="/bookings" 
            element={
              user ? <BookingsPage user={user} /> : <Navigate to="/" />
            } 
          />
          <Route 
            path="/messages" 
            element={
              user ? <MessagesPage user={user} /> : <Navigate to="/" />
            } 
          />
          <Route 
            path="/loyalty" 
            element={
              user ? <LoyaltyPage user={user} setUser={setUser} /> : <Navigate to="/rewards" replace />
            } 
          />
          <Route
            path="/rewards"
            element={
              <RewardsProgramPage
                user={user}
                onShowRegister={() => openAuthModal("register")}
                onShowAuth={() => openAuthModal("login")}
              />
            }
          />
          <Route path="/rewards10/:token" element={<WinBackOffer />} />
          <Route 
            path="/reviews" 
            element={<Navigate to="/customer-reviews" replace />} 
          />
          <Route 
            path="/profile" 
            element={
              user ? <ProfilePage user={user} onUpdate={fetchCurrentUser} /> : <Navigate to="/" />
            } 
          />
          <Route 
            path="/admin" 
            element={
              user?.is_admin ? <AdminDashboard user={user} userPerms={userPerms} /> : <Navigate to="/" />
            } 
          />
          <Route 
            path="/admin/communications" 
            element={hasPerm("email_controls") ? <AdminCommunications /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/leads" 
            element={hasPerm("leads") ? <AdminLeads /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/checklist-leads" 
            element={hasPerm("leads") ? <AdminChecklistLeads /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/settings" 
            element={hasPerm("settings") ? <AdminSettings /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/reviews" 
            element={user?.is_admin ? <AdminReviews /> : <Navigate to="/" />} 
          />
          <Route 
            path="/admin/email-activity" 
            element={hasPerm("email_controls") ? <AdminEmailActivity /> : <Navigate to="/admin" />} 
          />
          <Route
            path="/admin/slack-channels"
            element={user?.is_admin ? <AdminSlackChannels /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/home-depot"
            element={user?.is_admin ? <AdminHomeDepotPurchases /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/maintenance-proposals"
            element={user?.is_admin ? <AdminMaintenanceProposalsList /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/customer-history"
            element={user?.is_admin ? <AdminCustomerHistory /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/lsa-leads"
            element={user?.is_admin ? <AdminLSALeads /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/service-area"
            element={user?.is_admin ? <AdminServiceArea /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/landing-analytics"
            element={user?.is_admin ? <AdminLandingAnalytics /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/local-seo"
            element={user?.is_admin ? <AdminLocalSeo /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/local-seo-report"
            element={user?.is_admin ? <AdminLocalSeoReport /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/ai-visibility"
            element={user?.is_admin ? <AdminAIVisibility /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/gbp"
            element={user?.is_admin ? <AdminGbp /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/local-seo-projects"
            element={user?.is_admin ? <AdminLocalSeoProjects /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/local-authority"
            element={user?.is_admin ? <AdminLocalAuthority /> : <Navigate to="/admin" />}
          />
          <Route path="/projects/:slug" element={<ProjectCaseStudyPage />} />
          <Route
            path="/admin/stormready-leads"
            element={user?.is_admin ? <AdminStormReadyLeads /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/stormready-guide"
            element={user?.is_admin ? <AdminStormReadyGuide /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/stormready-v1"
            element={user?.is_admin ? <AdminStormReadyV1 /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/help-bot-training"
            element={user?.is_admin ? <AdminHelpBotTraining /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/stormready-builder"
            element={user?.is_admin ? <AdminStormReadyBuilder /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/no-ai"
            element={user?.is_admin ? <AdminNoAI /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/sona-calls"
            element={user?.is_admin ? <AdminSonaCalls /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/sona-knowledge"
            element={user?.is_admin ? <AdminSonaKnowledge /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/stormready-settings"
            element={user?.is_admin ? <AdminStormReadySettings /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/seasonal-banner"
            element={user?.is_admin ? <AdminSeasonalBanner /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/zelle"
            element={user?.is_admin ? <AdminZelleSettings /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/local-seo-photos"
            element={user?.is_admin ? <AdminLocalSeoPhotos /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/sops"
            element={user?.is_admin ? <AdminSops /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/sops/compliance"
            element={user?.is_admin ? <AdminSopCompliance /> : <Navigate to="/admin" />}
          />
          <Route
            path="/field/sops"
            element={user ? <TechnicianSops /> : <Navigate to="/" />}
          />
          <Route
            path="/admin/guides/pipeline"
            element={hasPerm("pipeline") ? <AdminPipelineGuide /> : <Navigate to="/admin" />}
          />
          <Route path="/terms" element={<TermsOfService />} />
          {/* Legacy path — permanently retired in favor of /terms.
              Client-side Navigate performs the visual redirect; the
              production reverse-proxy layer must add a true HTTP 301
              from /terms-conditions to /terms (see /app/memory/PRD.md). */}
          <Route path="/terms-conditions" element={<Navigate to="/terms" replace />} />
          <Route path="/terms-conditions/" element={<Navigate to="/terms" replace />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/gift-cards" element={<GiftCardsPage user={user} />} />
          <Route path="/gift-cards/success" element={<GiftCardsPage user={user} />} />
          <Route path="/gift-cards/redeem" element={<GiftCardsPage user={user} />} />
          <Route path="/maintenance-membership" element={<MaintenanceMembershipPage user={user} activeHome={activeHome} onShowAuth={() => openAuthModal("login")} />} />
          <Route path="/maintenance-membership/success" element={<MaintenanceMembershipPage user={user} activeHome={activeHome} onShowAuth={() => openAuthModal("login")} />} />
          <Route path="/maintenance-membership/cancel" element={<MaintenanceMembershipPage user={user} activeHome={activeHome} onShowAuth={() => openAuthModal("login")} />} />
          <Route path="/maintenance-proposal/:token" element={<MaintenanceProposalView />} />
          <Route path="/quo-contact-approval/:token" element={<QuoContactApprovalPage />} />
          <Route path="/47-point-checklist" element={<FortySevenPointChecklistPage />} />
          <Route path="/property-maintenance-terms" element={<PropertyMaintenanceTermsPage />} />
          <Route path="/property-health-check" element={<PropertyHealthCheckPage user={user} />} />
          <Route path="/how-credits-work" element={<HowCreditsWorkPage />} />
          <Route path="/getting-started" element={<GettingStartedPage />} />
          <Route path="/tv-mounting" element={<TVMountingCalculator user={user} activeHome={activeHome} onShowAuth={() => openAuthModal("login")} onShowRegister={() => openAuthModal("register")} />} />
          <Route path="/pressure-washing" element={<PressureWashingCalculator user={user} onShowAuth={() => openAuthModal("login")} onShowRegister={() => openAuthModal("register")} />} />
          <Route path="/window-cleaning" element={<WindowCleaningPage user={user} activeHome={activeHome} />} />
          <Route path="/cart" element={<CartPage user={user} onShowAuth={() => openAuthModal("login")} />} />
          <Route path="/cart/success" element={<CartPage user={user} onShowAuth={() => openAuthModal("login")} />} />
          <Route path="/my-estimates" element={user ? <MyEstimatesPage user={user} /> : <Navigate to="/" />} />
          
          {/* Customer Portal - Alias */}
          <Route path="/portal" element={user ? <CustomerPortal user={user} onRefresh={fetchCurrentUser} /> : <Navigate to="/" />} />
          <Route path="/portal/estimate/:estimateId" element={user ? <EstimateDetailPage user={user} /> : <Navigate to="/" />} />
          
          {/* Refer a Friend Page */}
          <Route 
            path="/refer" 
            element={user ? <ReferFriendPage user={user} /> : <Navigate to="/" />} 
          />
          
          {/* Guest Estimate Review - No auth required */}
          <Route path="/estimate/review/:token" element={<GuestEstimateReviewPage />} />
          <Route path="/guest/estimate/:token" element={<GuestEstimateReviewPage />} />
          <Route path="/v2-statement/:token" element={<V2StatementMagicLink />} />
          <Route path="/review/:token" element={<GuestEstimateReviewPage />} />
          <Route path="/pay/zelle" element={<ZellePaymentPage />} />
          
          {/* Partner Pages - No auth required */}
          <Route path="/become-a-partner" element={<BecomePartnerPage />} />
          <Route path="/partner-portal" element={<PartnerPortalPage />} />
          <Route path="/find-a-pro" element={<FindAProPage />} />
          
          {/* Service Area Pages */}
          <Route path="/service-areas" element={<ServiceAreasPage />} />
          <Route path="/service-areas/:citySlug" element={<CityServicePage />} />
          {/* Canonical service URL. Legacy /handyman-services/* redirects
              below (Aug 2026 migration). ServiceDetailPage emits the
              canonical link tag pointing here, and the sitemap now lists
              /services/* — so Google/AI crawlers consolidate ranking on
              the new URL. */}
          <Route path="/services/flood-barrier-rental" element={<StormReadyPage />} />
          <Route path="/services/flood-barrier-rental/harbor/:slug" element={<StormReadyHarborPage />} />
          <Route path="/stormready/release/:token" element={<StormReadyReleasePage />} />
          <Route path="/stormready/approve/:token" element={<StormReadyApprovePage />} />
          <Route path="/services/storm-prep-sandbagging-water-intrusion" element={<Navigate to="/services/flood-barrier-rental" replace />} />
          <Route path="/services/:serviceSlug" element={<ServiceDetailPage />} />
          <Route path="/handyman-services" element={<Navigate to="/services" replace />} />
          <Route path="/handyman-services/" element={<Navigate to="/services" replace />} />
          <Route path="/handyman-services/:serviceSlug" element={<HandymanServicesRedirect />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/admin/blog" element={<AdminBlogDrafts />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/bonus-guide" element={<BonusGuide />} />
          
          {/* Checkout Pages */}
          <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
          <Route path="/checkout/cancelled" element={<CheckoutCancelledPage />} />
          
          {/* Admin Payout Dashboard */}
          <Route 
            path="/admin/payouts" 
            element={hasPerm("bonus_calc") ? <PayoutDashboard /> : <Navigate to="/admin" />} 
          />
          
          {/* Admin Coupons & Promotions */}
          <Route 
            path="/admin/coupons" 
            element={hasPerm("coupons") ? <AdminCouponsPage user={user} /> : <Navigate to="/admin" />} 
          />
          
          {/* Admin Bonus Calc Dashboard */}
          <Route 
            path="/admin/bonus-calc" 
            element={hasPerm("bonus_calc") ? <AdminBonusCalc /> : <Navigate to="/admin" />} 
          />

          {/* Warehouse Inventory (Feb 2026) — QR-coded boxes/equipment/items */}
          <Route
            path="/admin/inventory"
            element={user?.is_admin || user?.is_employee ? <InventoryPage /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/inventory/label/:kind/:entity_id"
            element={user?.is_admin || user?.is_employee ? <InventoryLabel /> : <Navigate to="/admin" />}
          />
          {/* Tech-first mobile inventory (Feb 2026) — accessible to any
              authenticated employee for scan-and-transfer on job sites. */}
          <Route
            path="/inventory"
            element={user ? <InventoryMobile /> : <Navigate to="/login" />}
          />
          
          {/* Smart Estimate Builder */}
          <Route 
            path="/admin/smart-estimates" 
            element={hasPerm("smart_estimates") ? <SmartEstimatePage /> : <Navigate to="/admin" />} 
          />

          {/* V2 PARALLEL REBUILDS — Phase 1 placeholders (Feb 2026).
              V1 routes above remain canonical until V2 is validated in shadow mode. */}
          <Route
            path="/admin/smart-estimator-v2"
            element={hasPerm("smart_estimates") ? <SmartEstimatorV2 user={user} /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/bonus-calc-v2"
            element={hasPerm("bonus_calc") ? <BonusCalcV2 user={user} /> : <Navigate to="/admin" />}
          />
          
          {/* Admin Guide Hub */}
          <Route 
            path="/admin/guides" 
            element={hasPerm("guides") ? <AdminGuideHub /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/callbacks" 
            element={hasPerm("callbacks") ? <AdminCallbackManagement token={authToken} onBack={() => window.history.back()} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/revenue-forensics" 
            element={hasPerm("revenue_forensics") ? <AdminRevenueForensics token={authToken} onBack={() => window.history.back()} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/sms-settings" 
            element={hasPerm("sms") ? <AdminSMSSettings token={authToken} onBack={() => window.history.back()} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/call-analytics" 
            element={hasPerm("call_analytics") ? <AdminCallAnalytics token={authToken} onBack={() => window.history.back()} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/ai-agent" 
            element={hasPerm("ai_agent") ? <AdminAIAgent token={authToken} onBack={() => window.history.back()} /> : <Navigate to="/admin" />} 
          />
          <Route
            path="/admin/turno"
            element={user?.is_admin ? <AdminTurnoInspections /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/quo-approvals"
            element={user?.is_admin ? <AdminQuoContactApprovalsPage /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/partner-invitations"
            element={user?.is_admin ? <AdminPartnerInvitations /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/weekend-rewards"
            element={user?.is_admin ? <AdminWeekendRewards /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/turno-billing"
            element={user?.is_admin ? <AdminTurnoBilling /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/turno-nickname-fixer"
            element={user?.is_admin ? <TurnoNicknameFixer /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/bonus-calc-v1-1"
            element={user?.is_admin ? <AdminBonusCalcV11 /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/tech-time-report"
            element={user?.is_admin ? <AdminTechTimeReport /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/signals"
            element={user?.is_admin ? <AdminSignals /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/property-nicknames"
            element={user?.is_admin ? <AdminPropertyNicknames /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/ops/cutover"
            element={user?.is_admin ? <AdminOpsCutover /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/go-live"
            element={user?.is_admin ? <AdminGoLive /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/security"
            element={user?.is_admin ? <AdminSecurity /> : <Navigate to="/admin" />}
          />
          <Route
            path="/field/weekend-availability"
            element={user ? <EmployeeWeekendAvailability user={user} /> : <Navigate to="/login" />}
          />
          <Route
            path="/admin/weekend-calendar"
            element={<Navigate to="/field/weekend-availability" replace />}
          />
          <Route
            path="/admin/daily-kpis"
            element={user?.is_admin ? <AdminDailyKPIs /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/booking-funnel"
            element={user?.is_admin ? <BookingFunnelPage /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/ar-aging"
            element={user?.is_admin ? <AdminArAging /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/christina-report-card"
            element={user?.is_admin ? <AdminChristinaReportCard /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/equipment-registry"
            element={user?.is_admin ? <AdminEquipmentRegistry /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/fleet-tracker"
            element={user?.is_admin ? (
              <ErrorBoundary label="Fleet Tracker">
                <AdminFleetTracker />
              </ErrorBoundary>
            ) : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/callback-analytics"
            element={user?.is_admin ? (
              <ErrorBoundary label="Callback Analytics">
                <AdminCallbackAnalytics />
              </ErrorBoundary>
            ) : <Navigate to="/admin" />}
          />
          <Route 
            path="/admin/pipeline" 
            element={hasPerm("pipeline") ? <AdminPipeline token={authToken} onBack={() => window.history.back()} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/user-management" 
            element={hasPerm("user_management") ? <AdminUserManagement token={authToken} onBack={() => window.history.back()} /> : <Navigate to="/admin" />} 
          />
          <Route
            path="/admin/rewards-signups"
            element={user?.is_admin ? <AdminRewardsSignups onBack={() => window.history.back()} /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/integration-secrets"
            element={hasPerm("user_management") ? <AdminIntegrationSecrets onBack={() => window.history.back()} /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/cutover-guide"
            element={user?.is_admin ? <AdminCutoverGuide onBack={() => window.history.back()} /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/operations-guide"
            element={user?.is_admin ? <AdminOperationsGuide onBack={() => window.history.back()} /> : <Navigate to="/admin" />}
          />
          <Route
            path="/field/guide"
            element={(user?.is_technician || user?.is_admin) ? <TechnicianGuide onBack={() => window.history.back()} /> : <Navigate to="/" />}
          />
          {/* OC Tasker Company Dash — visible to any authenticated employee. */}
          <Route
            path="/company-dash"
            element={user ? <CompanyDash /> : <Navigate to="/" replace />}
          />
          {/* RouteOS — multi-tenant SaaS route optimization platform.
              Has its own auth (routeos_access_token) independent of OC Tasker's user. */}
          <Route path="/routeos/login" element={<RouteOSLogin />} />
          <Route path="/routeos" element={<RouteOSTechnicians />} />
          <Route path="/routeos/skills" element={<RouteOSSkills />} />
          <Route path="/routeos/vehicles" element={<RouteOSVehicles />} />
          <Route path="/routeos/customers" element={<RouteOSCustomers />} />
          <Route path="/routeos/properties" element={<RouteOSProperties />} />
          <Route path="/routeos/jobs" element={<RouteOSJobs />} />
          <Route path="/routeos/rules" element={<RouteOSRules />} />
          <Route path="/routeos/smart-fit" element={<RouteOSSmartFit />} />
          <Route path="/routeos/booking-admin" element={<RouteOSBookingAdmin />} />
          <Route path="/routeos/settings" element={<RouteOSSettings />} />
          {/* Public customer-facing booking — no auth required.
              Canonical URL: `/routeos/book/octasker`.
              Routemize-style alias `/routeos/@octasker` also supported. */}
          <Route path="/routeos/book/:slug" element={<RouteOSPublicBooking />} />
          <Route path="/routeos/@:slug" element={<RouteOSPublicBooking />} />
          {/* Booker v2 — 3-screen route-optimized. Ships alongside v1 for A/B. */}
          <Route path="/routeos/book-v2/:slug" element={<RouteOSPublicBookingV2 />} />
          {/* Facebook VSL ad landing page — funnels to the RouteOS
              booker with `service=handyman-estimate` preselected. */}
          <Route path="/free-estimate" element={<FreeEstimateLanding />} />
          <Route path="/routeos-admin" element={<RouteOSPlatform />} />
          {/* TV / wall-mounted mode. Same data, fullscreen presentation,
              auto-rotates through slides. Same auth gate as the standard dash. */}
          <Route
            path="/company-dash/tv"
            element={user ? <CompanyDashTv /> : <Navigate to="/" replace />}
          />
          <Route
            path="/admin/website-analytics"
            element={user?.is_admin ? <AdminWebsiteAnalytics onBack={() => window.history.back()} /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/team-structure"
            element={user?.is_admin ? <AdminTeamStructure onBack={() => window.history.back()} /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/hcp-employee-mapping"
            element={user?.is_admin ? <AdminHcpEmployeeMapping onBack={() => window.history.back()} /> : <Navigate to="/admin" />}
          />
          <Route
            path="/admin/maintenance-proposal-builder"
            element={user?.is_admin ? <AdminMaintenanceProposalBuilder /> : <Navigate to="/admin" />}
          />
          <Route 
            path="/admin/white-label" 
            element={hasPerm("user_management") ? <AdminWhiteLabel token={authToken} onBack={() => window.history.back()} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/tech-assistant" 
            element={hasPerm("ai_agent") ? <AdminTechAssistant token={authToken} onBack={() => window.history.back()} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/ai-audit-log" 
            element={hasPerm("ai_agent") ? <AdminAIAuditLog token={authToken} onBack={() => window.history.back()} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/campaigns" 
            element={hasPerm("pipeline") ? <AdminCampaigns token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/google-reviews" 
            element={hasPerm("pipeline") ? <AdminGoogleReviews token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/scorecard" 
            element={hasPerm("pipeline") ? <AdminScorecard token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/social-publish" 
            element={hasPerm("pipeline") ? <AdminSocialPublish token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/bonus-calc-v11" 
            element={hasPerm("pipeline") ? <AdminBonusCalcV11Shadow token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/bonus-calc-v1-1-live" 
            element={hasPerm("pipeline") ? <AdminBonusCalcLive token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/bonus-calc-v1-1-live-diagnostic" 
            element={hasPerm("pipeline") ? <AdminBonusCalcV11Live token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/marketplace" 
            element={hasPerm("campaigns") || hasPerm("pipeline") ? <AdminMarketplace token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/billing" 
            element={hasPerm("billing") || hasPerm("admin_dashboard") ? <AdminBilling token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/revenue-intelligence" 
            element={hasPerm("revenue_forensics") || hasPerm("admin_dashboard") ? <AdminRevenueIntelligence token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/todo" 
            element={hasPerm("admin_dashboard") ? <AdminTodo token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/integrations-health" 
            element={hasPerm("admin_dashboard") ? <AdminIntegrationsHealth token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/ai-safety" 
            element={hasPerm("admin_dashboard") ? <AdminAISafety token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/intent-analytics" 
            element={hasPerm("admin_dashboard") ? <AdminIntentAnalytics token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/timekeeping" 
            element={hasPerm("admin_dashboard") ? <AdminTimekeeping token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route
            path="/admin/time-off"
            element={hasPerm("admin_dashboard") ? <AdminTimeOff token={authToken} /> : <Navigate to="/admin" />}
          />
          <Route 
            path="/admin/job-hours" 
            element={hasPerm("admin_dashboard") ? <AdminJobHoursAudit token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/turno/quick-map" 
            element={hasPerm("admin_dashboard") ? <AdminTurnoQuickMap token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/turno/quick-map-queue" 
            element={hasPerm("admin_dashboard") ? <AdminQuickMapQueue token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/plan-tomorrow" 
            element={hasPerm("admin_dashboard") ? <AdminPlanTomorrow token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/hcp-mapping" 
            element={hasPerm("admin_dashboard") ? <AdminHcpMapping token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route 
            path="/admin/intelligence" 
            element={hasPerm("admin_dashboard") ? <AdminIntelligence token={authToken} /> : <Navigate to="/admin" />} 
          />
          <Route path="/help" element={<HelpPage token={authToken} />} />
          <Route path="/help/customer"  element={<HelpPage token={authToken} forceRole="customer"  />} />
          <Route path="/help/employee"  element={<HelpPage token={authToken} forceRole="employee"  />} />
          <Route path="/help/admin"     element={<HelpPage token={authToken} forceRole="admin"     />} />
          <Route path="/help/partner"   element={<HelpPage token={authToken} forceRole="partner"   />} />
          
          {/* Employee Bonus Portal — "My Pay" for technicians. They land here
              when they tap the My Pay button in /field. Used to redirect techs
              away to /field (legacy over-correction) which broke the button. */}
          <Route 
            path="/employee-portal" 
            element={
              loading ? (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
                </div>
              ) : user ? (
                <EmployeeBonusPortal user={user} />
              ) : (
                <EmployeePortalLogin onShowAuth={() => openAuthModal("login")} />
              )
            } 
          />

          {/* Field Dashboard — time clock + jobs + AI prep only. Safe to show in front of customers. */}
          <Route
            path="/field"
            element={
              loading ? (
                <div className="min-h-screen flex items-center justify-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
                </div>
              ) : user ? (
                <TechField user={user} />
              ) : (
                <EmployeePortalLogin onShowAuth={() => openAuthModal("login")} />
              )
            }
          />
          {/* SEO redirects — handles legacy octasker.com WordPress URLs.
              MUST be the LAST route so it only matches paths the named
              routes above didn't catch. See SeoRedirects.jsx for the
              full mapping table. */}
          <Route path="*" element={<SeoRedirects />} />
        </Routes>
          </Suspense>
        <FooterWrapper user={user} onShowRegister={() => openAuthModal("register")} />
        {deferredMountsReady && (
          <Suspense fallback={null}>
            <ChatbotWrapper user={user} />
          </Suspense>
        )}
        {/* Quiz Sticky Pill — inside Router because it reads useLocation() to
            gate visibility per route. Desktop-only floating pill for cold
            traffic; auto-hidden on hot-lead pages (/get-estimate, /quiz,
            admin, tech). Public-only. */}
        {!user && deferredMountsReady && (
          <Suspense fallback={null}>
            <QuizStickyPill />
          </Suspense>
        )}
      </BrowserRouter>
      </CartProvider>
      <AuthModal 
        open={showAuthModal} 
        onClose={() => setShowAuthModal(false)} 
        onLogin={handleLogin}
        initialMode={authMode}
      />
      {/* Lazy modals + PWA prompts — grouped under one Suspense (fallback null)
          so any one chunk can hydrate independently without blocking the
          others. Every child is gated by its own `open` flag or timer, so
          nothing renders (or fetches) until its trigger fires. */}
      <Suspense fallback={null}>
        <FirstLoginModal
          open={showFirstLoginModal}
          onComplete={handleFirstLoginComplete}
        />
        <HomeProfileModal
          open={showHomeProfileModal}
          onClose={handleHomeProfileSkip}
          onComplete={handleHomeProfileComplete}
          userName={user?.full_name || user?.name}
        />
        <WelcomeWhiteLabelModal
          open={showCutoverWelcome}
          onClose={() => setShowCutoverWelcome(false)}
          userName={(user?.full_name || "").split(" ")[0]}
          kind="cutover_guide"
        />
        <WelcomeWhiteLabelModal
          open={showOpsWelcome}
          onClose={() => setShowOpsWelcome(false)}
          userName={(user?.full_name || "").split(" ")[0]}
          kind="ops_guide"
        />
        <WelcomeWhiteLabelModal
          open={showTechWelcome}
          onClose={() => setShowTechWelcome(false)}
          userName={(user?.full_name || "").split(" ")[0]}
          kind="tech_guide"
        />
      </Suspense>
      <Toaster 
        position="top-center" 
        richColors 
        duration={5000}
        toastOptions={{
          style: {
            fontSize: '16px',
          },
          className: 'toast-center',
        }}
      />
      <style>{`
        [data-sonner-toaster] {
          top: 50% !important;
          transform: translateY(-50%) !important;
        }
      `}</style>
      
      {/* Email Capture Popup - Only shows for new visitors without accounts.
          Lazy + idle-gated so it doesn't compete with hero LCP on mobile. */}
      {!user && deferredMountsReady && (
        <Suspense fallback={null}>
          <EmailCapturePopup discountAmount={25} />
        </Suspense>
      )}
      
      {/* Cookie Consent Banner — public only. Logged-in users already
          accepted ToS at signup AND were having the banner overlap admin
          page bottom buttons (proposal builder Send button etc.). Deferred
          behind the idle-mount gate so it never blocks LCP on mobile. */}
      {!user && deferredMountsReady && !/^\/stormready\/(release|approve)\//.test(window.location.pathname) && (
        <Suspense fallback={null}>
          <CookieConsentBanner onAccept={initAllTracking} />
        </Suspense>
      )}
      {/* Service Worker — offline clock-in queue */}
      <ServiceWorkerBootstrap />
      {/* PWA Install Prompts — two variants, mutually exclusive gates.
          Staff prompt → techs & admins (immediate, 14d cooldown, work-centric copy)
          Customer prompt → everyone else (25s + scroll, 30d cooldown, booking-centric copy)
          Both lazy-loaded and gated behind the idle-mount latch. */}
      {deferredMountsReady && !/^\/stormready\/(release|approve)\//.test(window.location.pathname) && (
        <Suspense fallback={null}>
          <InstallPrompt user={user} />
          <CustomerInstallPrompt user={user} />
        </Suspense>
      )}
      {/* Floating "Ask OC Tasker" chat — admins only, every page except
          fullscreen wall-display modes where any chrome breaks the effect. */}
      {user?.is_admin && !window.location.pathname.startsWith("/company-dash/tv") && <AskOCTaskerWidget />}
    </div>
    </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
